import {useState, useEffect, useRef} from "react";
import { fetchNotes, postNote } from "./service/noteService";
import { useAuth } from "context/AuthContext";
import Snipper from "@components/ui/Snipper";
import { NotesList } from "./components/NotesList";
import { ApplicationNotesProps, Note } from "./models";
import { AddNote } from "./components/AddNote";
import { Badge } from "@components/shadcn-ui/badge";

export const ResourceNotes = ({
                                resourceId,
                                resourceType,
                              }: ApplicationNotesProps) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const { userDetails } = useAuth();
  const notesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (notesContainerRef.current) {
      notesContainerRef.current.scrollTop = notesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    const getNotes = async () => {
      try {
        const data = await fetchNotes(resourceId, resourceType);
        const sortedNotes = data.sort((a: Note, b: Note) =>
            Number(a.createDateTime) - Number(b.createDateTime)
        );
        setNotes(sortedNotes);
      } catch (error) {
        console.error("Error fetching notes:", error);
      } finally {
        setLoading(false);
      }
    };

    getNotes();
  }, [resourceId, resourceType]);

  useEffect(() => {
    if (!loading && notes.length > 0) {
      scrollToBottom();
    }
  }, [loading, notes.length]);

  const handlePostNote = async () => {
    if (!newNote.trim() || isSending) return;

    setIsSending(true);
    try {
      await postNote(resourceId, resourceType, newNote);
      setNotes((prevNotes) => [
        ...prevNotes,
        {
          resourceId: resourceId,
          resourceType: resourceType,
          notes: newNote,
          createDateTime: new Date().toISOString(),
          author: userDetails?.userName || "By You",
        },
      ]);
      setNewNote("");
    } catch (error) {
      console.error("Error posting note:", error);
    } finally {
      setIsSending(false);
    }
  };

  return (
      <div className="flex flex-col h-full"> {/* Container for entire notes section */}
        <div className="flex items-center justify-between bg-[#bbd0ce] p-3 rounded-t-lg">
          <h2 className="text-lg font-semibold">Notes</h2>
          <Badge variant="secondary">{notes.length}</Badge>
        </div>

        <div className="flex flex-col bg-gray-50 rounded-b-lg shadow-md border border-gray-200 min-h-[200px] max-h-[600px]">
          {loading ? (
              <div className="flex-1 flex justify-center items-center space-x-2">
                <Snipper/>
                <p>Loading notes...</p>
              </div>
          ) : (
              <>
                <div
                    ref={notesContainerRef}
                    className="flex-1 overflow-y-auto px-4 py-2" // Added padding for better spacing
                    style={{ height: 'calc(100% - 80px)' }} // Adjust based on your AddNote component height
                >
                  {notes.length ? (
                      <div className="space-y-4"> {/* Added spacing between notes */}
                        <NotesList notes={notes} userDetails={userDetails}/>
                      </div>
                  ) : (
                      <div className="h-full flex items-center justify-center">
                        <p className="text-center text-gray-500">
                          No notes are currently available for this application. You can
                          add one using the input box below!
                        </p>
                      </div>
                  )}
                </div>
                <div className="border-t border-gray-200 mt-auto"> {/* Fixed at bottom */}
                  <AddNote
                      newNote={newNote}
                      setNewNote={setNewNote}
                      handlePostNote={handlePostNote}
                      isSending={isSending}
                  />
                </div>
              </>
          )}
        </div>
      </div>
  );
};




